import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Gift, Download, Users, AlertTriangle, Pause, Play, Ban, Pencil } from "lucide-react";

const TIERS = ["prove", "build", "launch", "grow", "scale", "enterprise"];
const STATUS_COLORS = {
  active: "text-donkey-green", scheduled: "text-donkey-amber", expiring_soon: "text-donkey-amber",
  expired: "text-donkey-grey", revoked: "text-donkey-red", suspended: "text-donkey-amber",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function parseAllowances(text) {
  const out = {};
  (text || "").split("\n").forEach((line) => {
    const [k, v] = line.split("=").map((s) => (s || "").trim());
    if (k && v !== undefined && v !== "") out[k] = Number(v);
  });
  return Object.keys(out).length ? out : null;
}

export default function AdminComplimentary() {
  const [summary, setSummary] = useState(null);
  const [grants, setGrants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [q, setQ] = useState("");

  // create form
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("prove");
  const [start, setStart] = useState("");
  const [expiry, setExpiry] = useState("");
  const [noExpiry, setNoExpiry] = useState(false);
  const [reason, setReason] = useState("");
  const [campaign, setCampaign] = useState("");
  const [notes, setNotes] = useState("");
  const [allowances, setAllowances] = useState("");

  // bulk
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);

  const load = useCallback(async () => {
    const [s, g] = await Promise.all([
      api.get("/admin/comp/summary"),
      api.get("/admin/comp/grants", { params: { status: statusFilter, campaign: campaignFilter, q } }),
    ]);
    setSummary(s.data); setGrants(g.data.grants);
  }, [statusFilter, campaignFilter, q]);

  useEffect(() => { load(); }, [load]);

  const toIso = (d) => (d ? new Date(d).toISOString() : null);

  const createGrant = async () => {
    if (!email || !reason) return toast.error("Email and reason are required.");
    if (noExpiry && !window.confirm("No-expiry grant: this complimentary access will NOT expire automatically. Confirm?")) return;
    try {
      await api.post("/admin/comp/grants", {
        email, tier, start_date: toIso(start), expiry_date: toIso(expiry),
        no_expiry: noExpiry, confirm_no_expiry: noExpiry, reason, campaign, notes,
        allowances: parseAllowances(allowances),
      });
      toast.success("Complimentary grant created.");
      setEmail(""); setReason(""); setCampaign(""); setNotes(""); setAllowances(""); setExpiry(""); setNoExpiry(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create grant."); }
  };

  const previewBulk = async () => {
    if (noExpiry && !window.confirm("No-expiry bulk grant will NOT expire automatically. Confirm?")) return;
    try {
      const emails = bulkEmails.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/comp/grants/bulk/preview", {
        emails, tier, start_date: toIso(start), expiry_date: toIso(expiry),
        no_expiry: noExpiry, confirm_no_expiry: noExpiry, reason, campaign,
      });
      setBulkPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed."); }
  };

  const confirmBulk = async () => {
    try {
      const emails = bulkEmails.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/comp/grants/bulk", {
        emails, tier, start_date: toIso(start), expiry_date: toIso(expiry),
        no_expiry: noExpiry, confirm_no_expiry: noExpiry, reason, campaign,
        allowances: parseAllowances(allowances),
      });
      toast.success(`Created ${data.created} grant(s). ${data.unknown_emails.length} unknown, ${data.already_had_active_grant.length} already active.`);
      setBulkPreview(null); setBulkEmails(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk grant failed."); }
  };

  const act = async (gid, path, body) => {
    try {
      await api.post(`/admin/comp/grants/${gid}/${path}`, body || {});
      toast.success("Done.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
  };

  const editGrant = async (g) => {
    const newTier = window.prompt(`Change tier (current ${g.tier}). Options: ${TIERS.join(", ")}. Leave blank to keep.`, g.tier);
    if (newTier === null) return;
    const newExpiry = window.prompt("New expiry date (YYYY-MM-DD), blank to keep:", "");
    try {
      const body = {};
      if (newTier && newTier !== g.tier) body.tier = newTier;
      if (newExpiry) body.expiry_date = new Date(newExpiry).toISOString();
      await api.patch(`/admin/comp/grants/${g.id}`, body);
      toast.success("Grant updated.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Edit failed."); }
  };

  const revokeGrant = async (gid) => {
    const r = window.prompt("Reason for revoking this grant:");
    if (!r) return;
    act(gid, "revoke", { reason: r });
  };

  const exportCsv = () => {
    const url = `${API}/admin/comp/export.csv${campaignFilter ? `?campaign=${encodeURIComponent(campaignFilter)}` : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="admin-comp">
      {summary && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {Object.entries(summary.buckets).map(([k, v]) => (
            <div key={k} className="card-donkey rounded-xl p-3" data-testid={`comp-bucket-${k}`}>
              <p className={`font-mono text-xl ${STATUS_COLORS[k] || "text-donkey-warm"}`}>{v}</p>
              <p className="text-[10px] uppercase tracking-wider text-donkey-grey">{k.replace("_", " ")}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create single grant */}
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4"><Gift className="w-4 h-4 text-donkey-orange" /><h3 className="font-heading font-semibold text-donkey-warm text-sm">Grant complimentary access</h3></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user email" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="comp-email" />
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="comp-tier">
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-donkey-grey">Start (optional)</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="comp-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-donkey-grey">Expiry</label>
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} disabled={noExpiry} className="bg-donkey-deep border-white/15 text-donkey-warm disabled:opacity-40" data-testid="comp-expiry" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="campaign / source label" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="comp-campaign" />
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (required)" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="comp-reason" />
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="internal notes" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="comp-notes" />
          <label className="flex items-center gap-2 text-sm text-donkey-warm/90">
            <input type="checkbox" checked={noExpiry} onChange={(e) => setNoExpiry(e.target.checked)} data-testid="comp-no-expiry" />
            No expiry (confirm)
          </label>
        </div>
        <Textarea value={allowances} onChange={(e) => setAllowances(e.target.value)} placeholder="custom allowances (optional), one per line e.g. ai_interview=20" className="bg-donkey-deep border-white/15 text-donkey-warm mt-3 text-sm" rows={2} data-testid="comp-allowances" />
        <Button onClick={createGrant} className="bg-donkey-orange text-donkey-black font-semibold mt-3" data-testid="comp-create-btn">Create grant</Button>
      </div>

      {/* Bulk */}
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-donkey-orange" /><h3 className="font-heading font-semibold text-donkey-warm text-sm">Bulk campaign grant (uses the tier / dates / campaign / reason above)</h3></div>
        <Button type="button" variant="outline" onClick={() => {
          setTier("prove"); setNoExpiry(false); setCampaign("Honest Donkey Founding Testers");
          setReason(reason || "Founding tester complimentary access");
          const d = new Date(); d.setDate(d.getDate() + 30);
          setExpiry(d.toISOString().slice(0, 10));
        }} className="border-donkey-orange/40 bg-transparent text-donkey-orange mb-3" data-testid="comp-template-founding-testers">
          Use "Founding Testers" template (Prove · 30 days · max 30)
        </Button>
        <Textarea value={bulkEmails} onChange={(e) => setBulkEmails(e.target.value)} placeholder="paste emails, one per line or comma-separated" className="bg-donkey-deep border-white/15 text-donkey-warm text-sm" rows={3} data-testid="comp-bulk-emails" />
        <div className="flex gap-2 mt-3">
          <Button onClick={previewBulk} variant="outline" className="border-white/15 bg-transparent text-donkey-warm" data-testid="comp-bulk-preview-btn">Preview</Button>
        </div>
        {bulkPreview && (
          <div className="mt-3 p-4 rounded-xl bg-donkey-deep border border-white/10 text-sm" data-testid="comp-bulk-preview">
            <p className="text-donkey-warm mb-1">Will grant <span className="text-donkey-orange">{bulkPreview.tier_name}</span> to <span className="font-mono">{bulkPreview.grant_count}</span> user(s).</p>
            {bulkPreview.unknown_emails.length > 0 && <p className="text-donkey-red text-xs">Unknown (skipped): {bulkPreview.unknown_emails.join(", ")}</p>}
            {bulkPreview.already_has_active_grant.length > 0 && <p className="text-donkey-amber text-xs">Already active grant: {bulkPreview.already_has_active_grant.join(", ")}</p>}
            <Button onClick={confirmBulk} className="bg-donkey-orange text-donkey-black font-semibold mt-2" data-testid="comp-bulk-confirm-btn">Confirm {bulkPreview.grant_count} grant(s)</Button>
          </div>
        )}
      </div>

      {/* Filters + list */}
      <div className="flex flex-wrap gap-2 items-end">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="filter by email" className="bg-donkey-deep border-white/15 text-donkey-warm max-w-[200px]" data-testid="comp-filter-q" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="comp-filter-status">
          <option value="">all statuses</option>
          {["active", "scheduled", "expired", "revoked", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Input value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} placeholder="filter by campaign" className="bg-donkey-deep border-white/15 text-donkey-warm max-w-[200px]" data-testid="comp-filter-campaign" />
        <Button onClick={exportCsv} variant="outline" className="border-white/15 bg-transparent text-donkey-warm" data-testid="comp-export-btn"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>

      <div className="card-donkey rounded-2xl overflow-hidden">
        {grants.length === 0 && <p className="p-5 text-sm text-donkey-grey">No grants match.</p>}
        {grants.map((g) => (
          <div key={g.id} className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-white/5 last:border-0 text-sm" data-testid={`comp-grant-${g.id}`}>
            <span className="text-donkey-warm truncate flex-1 min-w-[160px]">{g.user_email}</span>
            <span className="text-donkey-orange capitalize">{g.tier_name}</span>
            <span className={`text-xs capitalize ${STATUS_COLORS[g.status] || "text-donkey-grey"}`}>{g.status}</span>
            {g.campaign && <span className="text-xs text-donkey-grey border border-white/10 rounded px-1.5 py-0.5">{g.campaign}</span>}
            <span className="text-xs text-donkey-grey">{g.no_expiry ? "no expiry" : `exp ${fmt(g.expiry_date)}`}</span>
            {g.status !== "revoked" && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => editGrant(g)} className="border-white/15 bg-transparent h-7 px-2 text-donkey-warm" data-testid={`comp-edit-${g.id}`}><Pencil className="w-3 h-3" /></Button>
                {g.status === "suspended"
                  ? <Button size="sm" variant="outline" onClick={() => act(g.id, "resume")} className="border-white/15 bg-transparent h-7 px-2 text-donkey-green" data-testid={`comp-resume-${g.id}`}><Play className="w-3 h-3" /></Button>
                  : <Button size="sm" variant="outline" onClick={() => act(g.id, "pause")} className="border-white/15 bg-transparent h-7 px-2 text-donkey-amber" data-testid={`comp-pause-${g.id}`}><Pause className="w-3 h-3" /></Button>}
                <Button size="sm" variant="outline" onClick={() => revokeGrant(g.id)} className="border-white/15 bg-transparent h-7 px-2 text-donkey-red" data-testid={`comp-revoke-${g.id}`}><Ban className="w-3 h-3" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-donkey-grey flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Complimentary access never creates a payment, invoice, refund or revenue record.</p>
    </div>
  );
}
